import type { JSX } from 'react';
import DSBox from '../../design-system/DSBox';
import DSAvatar from '../../design-system/DSAvatar';
import DSTypography from '../../design-system/Typography/DSTypography';
import { KlydoPreview } from '../../api/public';
import { KLYDO_BLACK, CLOCK_INFO_MUTED } from '../../consts/colors';
import { SOCIAL_NETWORKS, SocialMediaTypes } from '../../types/socialMedia';
import { extractSocialUsername } from '../../utils/socialMedia';
import { SocialMedia } from '../../api/user';
import { clockFacePx, clockFaceSize } from './clockFaceScale';

type ArtistInfoProps = {
  klydoPreview: KlydoPreview;
  scale?: number;
};

const MAX_ITEMS_PER_ROW = 2;
const MAX_ROWS = 2;

const chunk = <T,>(items: T[], size: number): T[][] => {
  const rows: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    rows.push(items.slice(i, i + size));
  }
  return rows;
};

/**
 * "Artist" tab — information about the artist.
 * Ported 1:1 from the v3 Kotlin/Compose AboutSettingsPage (1080px face reference):
 * artist photo → name → social media (2 per row, up to 2 rows) → bio.
 */
const ArtistInfo = ({
  klydoPreview,
  scale = 1,
}: ArtistInfoProps): JSX.Element => {
  const { author: artist } = klydoPreview;

  const validSocialMedia = (artist?.socialMedia || [])
    .filter(
      (item) =>
        SOCIAL_NETWORKS.some(
          (network) =>
            network.name.toLowerCase() === item.network.toLowerCase(),
        ) &&
        item.network.toLowerCase() !== SocialMediaTypes.WEBSITE.toLowerCase(),
    )
    .slice(0, MAX_ITEMS_PER_ROW * MAX_ROWS);

  const socialRows = chunk(validSocialMedia, MAX_ITEMS_PER_ROW);
  const socialIconSize = clockFaceSize(38, scale);

  const renderSocialItem = (item: SocialMedia) => {
    const socialNetwork = SOCIAL_NETWORKS.find(
      (network) => network.name.toLowerCase() === item.network.toLowerCase(),
    );
    if (!socialNetwork) return null;

    const IconComponent = socialNetwork.icon;
    return (
      <DSBox
        key={item.network}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: clockFacePx(10, scale),
        }}
      >
        <IconComponent
          width={socialIconSize}
          height={socialIconSize}
          style={{ color: CLOCK_INFO_MUTED }}
        />
        <DSTypography
          sx={{
            fontFamily: 'Gilroy-Light',
            fontSize: clockFacePx(38, scale),
            color: CLOCK_INFO_MUTED,
          }}
        >
          {extractSocialUsername(item)}
        </DSTypography>
      </DSBox>
    );
  };

  return (
    <DSBox
      sx={{
        width: '100%',
        height: '100%',
        borderRadius: '50%',
        overflow: 'hidden',
        background: KLYDO_BLACK,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        paddingX: clockFacePx(40, scale),
        paddingTop: clockFacePx(30, scale),
        textAlign: 'center',
      }}
    >
      <DSAvatar
        src={artist?.photoUrl}
        alt={artist?.name}
        sx={{
          width: clockFacePx(150, scale),
          height: clockFacePx(150, scale),
          borderRadius: '50%',
        }}
      />

      <DSTypography
        sx={{
          marginTop: clockFacePx(30, scale),
          fontFamily: 'Gilroy-Bold',
          color: '#FFFFFF',
          fontSize: clockFacePx(70, scale),
          lineHeight: 1.1,
        }}
      >
        {artist?.name}
      </DSTypography>

      {socialRows.length > 0 && (
        <DSBox
          sx={{
            marginTop: clockFacePx(30, scale),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: clockFacePx(15, scale),
            paddingX: clockFacePx(20, scale),
          }}
        >
          {socialRows.map((row, index) => (
            <DSBox
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: clockFacePx(20, scale),
              }}
            >
              {row.map(renderSocialItem)}
            </DSBox>
          ))}
        </DSBox>
      )}

      {artist?.description && (
        <DSTypography
          sx={{
            marginTop: clockFacePx(30, scale),
            fontFamily: 'Gilroy-Light',
            color: '#FFFFFF',
            fontSize: clockFacePx(38, scale),
            lineHeight: 1.2,
            paddingX: clockFacePx(20, scale),
          }}
        >
          {artist.description}
        </DSTypography>
      )}
    </DSBox>
  );
};

export default ArtistInfo;
